/**
 * Purchases Summary dashboard widget (Story 6-5-5).
 * Displays purchases total for last 7 days, current month, and last month.
 * Admin and manager – sensitive financial data per NFR-S8.
 */
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { getPurchasesDashboardSummary } from "../../services/purchaseService";
import { formatCurrency } from "../../utils/currency";
import type { PurchasesSummary as PurchasesSummaryType } from "../../types/models";
import { getApiErrorMessage } from "../../utils/apiError";

const PERIOD_LABELS = {
  last_7_days: "summary_last_7_days",
  current_month: "summary_current_month",
  last_month: "summary_last_month",
} as const;

export function PurchasesSummary() {
  const { t } = useTranslation("purchases");
  const [summary, setSummary] = useState<PurchasesSummaryType | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    getPurchasesDashboardSummary()
      .then((data) => {
        if (!cancelled) setSummary(data);
      })
      .catch((err) => {
        if (!cancelled) {
          const msg = getApiErrorMessage(err, t("summary_error"));
          setError(msg);
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center py-8 text-center">
        <div className="w-8 h-8 border-2 border-secondary-500 border-t-transparent rounded-full animate-spin mb-3" />
        <p className="text-sm text-neutral-500 dark:text-neutral-400">{t("summary_loading")}</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="py-4 text-center">
        <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
      </div>
    );
  }

  if (!summary) {
    return (
      <div className="py-4 text-center">
        <p className="text-sm text-neutral-500 dark:text-neutral-400">{t("summary_no_data")}</p>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      <div className="flex justify-between items-baseline gap-2">
        <span className="text-xs text-neutral-500 dark:text-neutral-400">{t("summary_today")}</span>
        <span className="text-sm font-semibold text-neutral-900 dark:text-neutral-100 tabular-nums">
          {summary.today_count ?? 0} – {formatCurrency(summary.today ?? 0)}
        </span>
      </div>
      {(["last_7_days", "current_month", "last_month"] as const).map((key) => {
        const countKey = `${key}_count` as const;
        const count = summary[countKey] ?? 0;
        return (
          <div key={key} className="flex justify-between items-baseline gap-2">
            <span className="text-xs text-neutral-500 dark:text-neutral-400">
              {t(PERIOD_LABELS[key])}
            </span>
            <span className="text-sm font-semibold text-neutral-900 dark:text-neutral-100 tabular-nums">
              {count} – {formatCurrency(summary[key] ?? 0)}
            </span>
          </div>
        );
      })}
      {(summary.pending_funding_count ?? 0) > 0 && (
        <div className="flex justify-between items-center gap-2 pt-1.5 border-t border-neutral-200 dark:border-neutral-700">
          <span className="text-xs text-amber-600 dark:text-amber-400 font-medium">
            {t("summary_pending_funding")}
          </span>
          <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-semibold bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300">
            {summary.pending_funding_count}
          </span>
        </div>
      )}
    </div>
  );
}
